import useMediaQuery from "@/custom_hooks/mediaQuery";
import cx from "classnames";
import { Plus } from "lucide-react";
import { Icons } from "@/assets";
import { FC, useState, useEffect } from "react";
import Container from "@/components/Container";
import { useCart } from "@/components/UI/Buttons/CartContext";
import useMetaPixel from "@/custom_hooks/useMetaPixel";
import CollapsibleItem from "@/components/Collapsible/CollapsibleItem";
import { Input, Select } from "@/components/UI";
import PhoneInput from "@/components/UI/PhoneInput";
import { Button } from "@/components/UI/Buttons/Button";
import { ResponseError } from "@/data";
import ordersService from "@/services/ordersService";
import { useNavigate } from "react-router-dom";
import ROUTES from "@/data/Routes";
import { useAuth } from "@/data/context/authContext";
import { toast } from "sonner";
import { Order } from "@/data/interfaces/Order";
import { isPhoneValid, isValidEmail } from "@/utils/formValidator";
import { formatUserFullNameForDisplay } from "@/utils/isUserInfoFilled";
import { pushEvent } from "@/utils/gtm";
import { ROLES } from "@/data/interfaces/RolesEnum";

interface OrderResponse {
  checkout_url?: string;
  status?: "paid";
  order?: Order;
  /** Guest 100% discount: redirect to registration with order_id (no Stripe session). */
  redirect_to?: string;
}

const city = ["Vilnius", "Kaunas", "Šiauliai", "Panevėžys", "Mažeikiai"];

type CheckoutFieldErrors = {
  full_name?: string;
  email?: string;
  phone?: string;
  city?: string;
  recipients?: string;
  /** Cart / server / unknown keys */
  general?: string;
};

function firstValidationMessage(value: unknown): string | undefined {
  if (Array.isArray(value) && value.length > 0) return String(value[0]);
  if (typeof value === "string") return value;
  return undefined;
}

/** Map Laravel `errors` keys to checkout fields — avoids random `for…in` order showing wrong copy. */
function parseOrderApiErrorsBody(body: Record<string, unknown>): CheckoutFieldErrors {
  const out: CheckoutFieldErrors = {};
  const errors = body.errors;
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    if (typeof body.message === "string" && body.message.trim()) {
      out.general = body.message;
    }
    return out;
  }

  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    const msg = firstValidationMessage(value);
    if (!msg) continue;
    if (key === "full_name") out.full_name = msg;
    else if (key === "email") out.email = msg;
    else if (key === "phone") out.phone = msg;
    else if (key === "city") out.city = msg;
    else if (key.startsWith("recipients")) {
      out.recipients = out.recipients ? `${out.recipients} · ${msg}` : msg;
    } else if (key === "cart_uuid") {
      out.general = msg;
    } else {
      out.general = out.general ? `${out.general} · ${msg}` : msg;
    }
  }

  if (
    Object.keys(out).length === 0 &&
    typeof body.message === "string" &&
    body.message.trim()
  ) {
    out.general = body.message;
  }
  return out;
}

const FieldError: FC<{ message?: string }> = ({ message }) =>
  message ? (
    <p className="mt-1 text-sm text-red-600" role="alert">
      {message}
    </p>
  ) : null;

const formatEuro = (value?: number | string) =>
  value === undefined || value === null || value === "" ? "0€" : `${value}€`;

// interface CartItemResponse {
//     id: number;
//     discounted_line_total_cents: number | undefined;
//     line_total_cents: number;
//     product: {
//         id: number;
//         name: string;
//         price_cents: number;
//         image_url: string;
//     };
//     quantity: number;
// }

// interface CartResponse {
//     id: string;
//     items: CartItemResponse[];
//     created_at: string;
//     updated_at: string;
// }

const Checkout: FC = () => {
  const {
    cartItems,
    updateQuantity,
    cartId,
    clearCart,
    removeFromCart,
    totals,
  } = useCart();
  const { loadUser, user, productToRegisterFor } = useAuth();
  const navigate = useNavigate();
  const metaPixel = useMetaPixel();
  const isMobile = useMediaQuery(768);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<CheckoutFieldErrors>({});
  const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);
  const [forOtherPersonIds, setForOtherPersonIds] = useState<number[]>([]);
  const [itemRecipients, setItemRecipients] = useState<
    Record<number, Array<{ email: string; city: string }>>
  >({});
  const [formData, setFormData] = useState({
    full_name: user ? formatUserFullNameForDisplay(user) : "",
    email: user?.email ?? "",
    phone: user?.phone ?? "",
    phoneCountry: "LT",
    city: "",
  });

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        full_name: formatUserFullNameForDisplay(user),
        email: user.email,
        phone: user.phone,
      }));
    }
  }, [user]);

  const trackPurchase = async (order: Order) => {
    try {
      metaPixel.trackPurchase(order, window.location.href);
      pushEvent("purchased", {
        transaction_id: order.id,
        value: order.total_cents / 100,
        currency: "EUR",
        item: cartItems.map((item) => item.name).join(", ") || "",
      });
    } catch (error) {
      console.error("FAILED TO TRACK PURCHASE", error);
    }
  };

  // Check for successful payment and clear cart
  useEffect(() => {
    _handlePurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _handlePurchase = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");

    if (status === "success") {
      const giftId = urlParams.get("gift_id");

      if (giftId) {
        toast.success("Dovanų kuponas sėkmingai nupirktas!", {
          description: "Kuponas bus išsiųstas jūsų nurodytu el. paštu",
          duration: 8000,
        });

        navigate("/", {
          state: {
            purchaseSuccess: true,
            giftId: giftId,
          },
        });
        return;
      }

      const order = await _getOrder();
      await trackPurchase(order);
      clearCart();

      await loadUser();
      _redirectAfterPurchase(order);
    }
  };

  const _redirectAfterPurchase = (order: Order) => {
    if (!hasShownSuccessToast) {
      toast.success("Užsakymas sėkmingai apmokėtas!", { duration: 5000 });
      setHasShownSuccessToast(true);
    }

    let destination = ROUTES.HOME;
    const state: any = {};

    if (order.user_id && user) {
      if (
        !user.email_verified_at ||
        (user.role === ROLES.USER && (!user.type_form_submitted || productToRegisterFor))
      ) {
        destination = ROUTES.HOME;
      } else {
        destination = ROUTES.HOME;
      }
    } else {
      const [name, surname] = order.full_name.split(" ");
      state.email = order.email;
      state.name = name;
      state.surname = surname;
      state.phone = order.phone;
    }

    navigate(destination, { state });
  };

  const _getOrder = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get("order_id")!;
    return await ordersService.getOrder(orderId);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (
        name === "full_name" ||
        name === "email" ||
        name === "phone" ||
        name === "city"
      ) {
        delete next[name];
      }
      delete next.general;
      return next;
    });
  };

  const increaseQuantity = async (id: number) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item) return;

    try {
      await updateQuantity(id, item.quantity + 1);
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  const decreaseQuantity = async (id: number) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item || item.quantity <= 1) return;

    try {
      await updateQuantity(id, item.quantity - 1);
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };

  const toggleForOtherPerson = (id: number) => {
    setForOtherPersonIds((prev) => {
      const isRemoving = prev.includes(id);
      if (isRemoving) {
        setItemRecipients((prevRecs) => {
          const newRecs = { ...prevRecs };
          delete newRecs[id];
          return newRecs;
        });
        return prev.filter((item) => item !== id);
      } else {
        setItemRecipients((prevRecs) => ({
          ...prevRecs,
          [id]: [{ email: "", city: "" }],
        }));
        return [...prev, id];
      }
    });
  };

  const addRecipient = (itemId: number) => {
    setItemRecipients((prev) => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), { email: "", city: "" }],
    }));
  };

  const removeRecipientAt = (itemId: number, index: number) => {
    setItemRecipients((prev) => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, i) => i !== index),
    }));
  };

  const updateRecipient = (
    itemId: number,
    index: number,
    field: "email" | "city",
    value: string,
  ) => {
    setItemRecipients((prev) => ({
      ...prev,
      [itemId]: prev[itemId].map((rec, i) =>
        i === index ? { ...rec, [field]: value } : rec,
      ),
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.recipients;
      return next;
    });
  };

  const handleCheckout = async () => {
    const nextErrors: CheckoutFieldErrors = {};

    if (!cartId) {
      nextErrors.general =
        "Krepšelis nerastas. Atnaujinkite puslapį arba pridėkite prekę iš naujo.";
      setFieldErrors(nextErrors);
      return;
    }

    if (!formData.full_name?.trim()) {
      nextErrors.full_name = "Įveskite vardą ir pavardę";
    }
    if (!formData.email?.trim()) {
      nextErrors.email = "Įveskite el. paštą";
    } else if (!isValidEmail(formData.email)) {
      nextErrors.email = "Netinkamas el. pašto formatas";
    }
    if (!formData.phone?.trim()) {
      nextErrors.phone = "Įveskite telefono numerį";
    } else if (!isPhoneValid(formData.phone.trim())) {
      nextErrors.phone = "Neteisingas telefono numerio formatas";
    } else if (!isPhoneValid(formData.phone.trim(), formData.phoneCountry)) {
      nextErrors.phone = "Telefono numeris neatitinka pasirinktos šalies";
    }
    if (!formData.city) {
      nextErrors.city = "Pasirinkite miestą";
    }

    const recipients = forOtherPersonIds.flatMap((id) => {
      const recs = itemRecipients[id] || [];
      return recs.map((r) => ({
        cart_item_id: id,
        email: r.email.trim(),
        city: r.city,
        quantity: 1,
      }));
    });

    for (const recipient of recipients) {
      if (!recipient.email || !recipient.city) {
        nextErrors.recipients =
          "Užpildykite kiekvieno kito asmens el. paštą ir miestą";
        break;
      }
      if (!isValidEmail(recipient.email)) {
        nextErrors.recipients =
          "Netinkamas kito asmens el. pašto formatas";
        break;
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setIsLoading(true);
    setFieldErrors({});

    try {
      const baseApiUrl = import.meta.env.VITE_BASE_API_URL;

      const response = await fetch(`${baseApiUrl}/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          cart_uuid: cartId,
          email: formData.email,
          phone: formData.phone,
          full_name: formData.full_name,
          city: formData.city,
          recipients: recipients,
        }),
      });

      if (!response.ok) {
        let body: Record<string, unknown> = {};
        try {
          body = (await response.json()) as Record<string, unknown>;
        } catch {
          body = {};
        }
        const parsed = parseOrderApiErrorsBody(body);
        setFieldErrors(
          Object.keys(parsed).length > 0
            ? parsed
            : {
                general:
                  "Nepavyko sukurti užsakymo. Patikrinkite duomenis ir bandykite dar kartą.",
              },
        );
        throw new Error("Failed to create order");
      }

      const data = (await response.json()) as OrderResponse;

      if (data.redirect_to) {
        if (data.order) await trackPurchase(data.order);
        clearCart();
        await loadUser();
        setIsLoading(false);
        if (
          data.redirect_to.startsWith("http://") ||
          data.redirect_to.startsWith("https://")
        ) {
          window.location.href = data.redirect_to;
        } else {
          navigate(data.redirect_to);
        }
        return;
      }

      if (data.status === "paid" && data.order) {
        await trackPurchase(data.order);
        clearCart();
        await loadUser();
        _redirectAfterPurchase(data.order);
        return;
      }

      if (!data.checkout_url) {
        throw new Error("No checkout URL received");
      }

      // Remove the clearCart() call here since we'll handle it after successful payment
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error("Error creating order:", error);
      // setError('Failed to process order. Please try again.');
    }
    setIsLoading(false);
  };

  return (
    <section className="bg-canvas px-4 pb-0 md:px-6 md:pb-20">
      <Container className="pt-24 md:pt-32">
        <div
          className={cx(
            "grid items-start gap-6 md:grid-cols-[minmax(0,1fr)_minmax(340px,0.92fr)]",
            isMobile ? "mb-8" : "mb-16",
          )}
        >
          <div className={cx(isMobile && "mb-2")}>
            <div
              className={cx(
                "border-line rounded-[24px] border bg-white shadow-[0_16px_50px_rgba(24,24,24,0.05)]",
                isMobile ? "px-4 py-6" : "px-8 py-8",
              )}
            >
              <label className="text-ink text-[26px] leading-[1.15] font-medium md:text-[28px]">
                Užpildykite užsakymo formą
              </label>
              <div className="space-y-4 pt-8">
                <div>
                  <Input
                    label="Vardas ir pavardė*"
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    disabled={!!user}
                  />
                  <FieldError message={fieldErrors.full_name} />
                </div>

                <div>
                  <Input
                    label="El. paštas*"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!!user}
                  />
                  <FieldError message={fieldErrors.email} />
                </div>

                <div className="pb-6">
                  <PhoneInput
                    label="Tel. numeris*"
                    id="phone"
                    name="phone"
                    value={formData.phone ?? ""}
                    onChange={(val) => {
                      setFormData((prev) => ({ ...prev, phone: val }));
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.phone;
                        delete next.general;
                        return next;
                      });
                    }}
                    onCountryChange={(c) =>
                      setFormData((prev) => ({ ...prev, phoneCountry: c ?? "LT" }))
                    }
                    disabled={!!user}
                    error={fieldErrors.phone}
                  />
                </div>
              </div>

              <div className="pt-1">
                <p className="text-muted mb-3 text-sm">
                  Pasirinkite jums tinkamiausią miestą*
                </p>

                {city.map((cityName) => (
                  <Input
                    key={cityName}
                    type="radio"
                    name="city"
                    value={cityName}
                    label={cityName}
                    checked={formData.city === cityName}
                    onChange={handleInputChange}
                    className="mb-2"
                    hideNativeInput={true}
                  />
                ))}
                <FieldError message={fieldErrors.city} />
              </div>

              {fieldErrors.general && (
                <div className="mt-4 text-sm text-red-600" role="alert">
                  {fieldErrors.general}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="border-line flex min-h-[612px] flex-col justify-between rounded-[24px] border bg-white px-4 py-6 shadow-[0_16px_50px_rgba(24,24,24,0.05)] md:px-8 md:py-8">
              <div className="space-y-5">
                {cartItems.length === 0 && (
                  <div className="border-line text-muted rounded-[20px] border bg-white px-5 py-6 text-sm">
                    Krepšelis tuščias. Pridėkite pasirinktą programą ir čia
                    matysite užsakymo suvestinę.
                  </div>
                )}

                {cartItems.map((item) => (
                  <div key={item.id}>
                    <div className="border-line border-b pb-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-5">
                          <div className="bg-paper border-ink grid size-11 place-items-center rounded-full border">
                            <img
                              src={Icons.Lighting}
                              alt=""
                              aria-hidden="true"
                              className="size-6 object-contain"
                            />
                          </div>
                          <p className="text-ink text-[18px] leading-tight font-medium md:text-[20px]">
                            {item.name}
                          </p>
                        </div>
                        <div className="text-ink flex flex-col text-right text-[18px] font-medium md:flex-row md:text-[20px]">
                          {item.preTotal && (
                            <div className="text-muted min-w-[90px] line-through">
                              {formatEuro(item.preTotal)}
                            </div>
                          )}
                          <div className="min-w-[90px]">
                            {formatEuro(item.total)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center gap-4">
                          <label className="text-muted text-base font-normal md:text-[18px]">
                            Kiekis:
                          </label>
                          <div className="border-line-strong flex h-12 w-[112px] items-center justify-center gap-2 rounded-full border bg-white px-1">
                            <button
                              className="text-ink flex h-10 w-10 items-center justify-center rounded-full text-2xl transition hover:bg-[#F3F1EC]"
                              onClick={() => decreaseQuantity(item.id)}
                            >
                              −
                            </button>
                            <span className="text-ink w-6 text-center text-[18px] font-medium">
                              {item.quantity}
                            </span>
                            <button
                              className="text-ink flex h-10 w-10 items-center justify-center rounded-full text-2xl transition hover:bg-[#F3F1EC]"
                              onClick={() => increaseQuantity(item.id)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-4">
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-muted hover:text-ink text-[15px] font-normal transition md:text-[16px]"
                          >
                            Pašalinti
                          </button>
                          <Button
                            variant="muted"
                            size="chip"
                            onClick={() =>
                              toggleForOtherPerson(item.cart_item_id || 0)
                            }
                            className="hover:text-ink"
                          >
                            Perkate kitam asmeniui?
                          </Button>
                        </div>
                        {forOtherPersonIds.includes(item.cart_item_id || 0) && (
                          <div className="bg-surface mt-5 space-y-6 rounded-[20px] p-4">
                            <FieldError message={fieldErrors.recipients} />
                            {(itemRecipients[item.cart_item_id || 0] || []).map(
                              (recipient, index) => (
                                <div key={index} className="">
                                  <div className="mb-4 flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-700">
                                      {index + 1} asmuo, kuriam bus atliekami
                                      tyrimai
                                    </p>
                                    {index > 0 && (
                                      <button
                                        onClick={() =>
                                          removeRecipientAt(
                                            item.cart_item_id || 0,
                                            index,
                                          )
                                        }
                                        className="text-red-500 hover:text-red-700"
                                        title="Pašalinti asmenį"
                                      >
                                        <Icons.MinusCircle size={20} />
                                      </button>
                                    )}
                                  </div>
                                  <div className="space-y-4">
                                    <Input
                                      label="El. paštas"
                                      type="email"
                                      value={recipient.email}
                                      onChange={(event) =>
                                        updateRecipient(
                                          item.cart_item_id || 0,
                                          index,
                                          "email",
                                          event.target.value,
                                        )
                                      }
                                      placeholder="vardas@el.paštas.lt"
                                    />
                                    <Select
                                      label="Miestas"
                                      value={recipient.city}
                                      onValueChange={(value: string) =>
                                        updateRecipient(
                                          item.cart_item_id || 0,
                                          index,
                                          "city",
                                          value,
                                        )
                                      }
                                      options={city.map((c) => ({
                                        label: c,
                                        value: c,
                                      }))}
                                      placeholder="Pasirinkite miestą"
                                    />
                                  </div>
                                </div>
                              ),
                            )}

                            <div
                              className="flex justify-center"
                              title={
                                (itemRecipients[item.cart_item_id || 0] || [])
                                  .length >= item.quantity
                                  ? `Negalima pridėti daugiau asmenų nei pasirinktas kiekis (${item.quantity})`
                                  : "Pridėti kitą asmenį"
                              }
                            >
                              <button
                                onClick={() =>
                                  addRecipient(item.cart_item_id || 0)
                                }
                                disabled={
                                  (itemRecipients[item.cart_item_id || 0] || [])
                                    .length >= item.quantity
                                }
                                className={cx(
                                  "flex h-10 w-10 items-center justify-center rounded-full transition-all",
                                  (itemRecipients[item.cart_item_id || 0] || [])
                                    .length >= item.quantity
                                    ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                    : "bg-primary text-white hover:scale-110",
                                )}
                              >
                                <Plus size={24} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-6">
                <Discount />

                <div className="mb-4 flex items-center justify-between">
                  <span className="text-ink text-[20px] font-medium">
                    Bendra suma
                  </span>
                  <div className="text-ink flex flex-col text-[20px] font-medium md:flex-row">
                    {totals?.preTotal && (
                      <div className="text-muted min-w-[90px] line-through">
                        {formatEuro(totals.preTotal)}
                      </div>
                    )}
                    <div className="min-w-[90px]">
                      {formatEuro(totals?.total)}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={isLoading || !cartItems.length}
                  variant="ink"
                  size="checkout"
                  onHover
                  hoverLight
                  className="h-[59px] p-[18px]"
                >
                  {isLoading ? "Apdorojama..." : "Apmokėti"}
                </Button>
                <p className="text-muted mt-4 text-start text-xs">
                  Paspaudus užsakymą, jūs sutinkate su mūsų{" "}
                  <a href="/privatumo-politika" className="underline">
                    privatumo politika
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Checkout;

const Discount = () => {
  const { applyDiscountCode, discount } = useCart();
  const [isDiscountTabOpen, setIsDiscountTabOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState<string | undefined>(
    discount?.code,
  );
  const [message, setMessage] = useState<{ message?: string; error?: string }>(
    {},
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setDiscountCode(discount?.code);
  }, [discount]);

  const applyDiscount = async () => {
    setMessage({});
    const code = discountCode?.trim();
    if (code && code.length > 0) {
      try {
        setIsLoading(true);
        const message = await applyDiscountCode(code);
        setMessage({ message });
      } catch (error: any) {
        if (error instanceof ResponseError) {
          setMessage({ error: error.message });
        } else if (error?.response?.status === 429) {
          setMessage({
            error: "Per daug bandymų. Prašome palaukti ir bandyti dar kartą.",
          });
        } else {
          console.error(error);
          setMessage({ error: error.message || "Įvyko klaida" });
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setMessage({ error: "Įveskite nuolaidos kodą" });
    }
  };

  return (
    <CollapsibleItem
      label="Nuolaidų kodai ir dovanų kortelės"
      isOpen={isDiscountTabOpen}
      onToggle={() => setIsDiscountTabOpen(!isDiscountTabOpen)}
      isLast
      className="mt-0 mb-8"
      description={
        <>
          <div className="flex w-full flex-col items-stretch gap-4 md:flex-row md:items-end md:justify-center">
            <div className="w-full self-stretch md:flex-1">
              <Input
                label="Kodas"
                type="text"
                placeholder="Įveskite kodą"
                className="w-full"
                inputClassName="!h-15 text-lg w-full"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
              />
            </div>
            <Button
              onClick={applyDiscount}
              className="mt-2 h-15 w-full self-stretch md:mt-0 md:w-auto md:min-w-[140px] md:self-auto"
              variant="ink"
              disabled={isLoading}
              size="chip"
            >
              Pritaikyti
            </Button>
          </div>
          {message.error && (
            <p className="text-center text-lg text-red-700">{message.error}</p>
          )}
          {message.message && (
            <p className="text-center text-lg text-green-700">
              {message.message}
            </p>
          )}
        </>
      }
    />
  );
};
